import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import "./FloatingToolbar.scss";
import { MODES } from "../NodeEditor";

import HandStop from "./assets/Hand Stop.svg";
import Select from "./assets/Select.svg";
import Undo from "./assets/Undo.svg";
import Redo from "./assets/Redo.svg";

// A floating toolbar that starts initially in the center on the x axis and 3 quarters down on the y axis.
// The toolbar can be dragged around within the bounds of its parent container.
const FloatingToolbar = (props) => {
    const [toolbarX, setToolbarX] = useState(props.initialX ?? window.innerWidth / 2); // 0.0 to 1.0 (percentage of parent width)
    const [toolbarY, setToolbarY] = useState(props.initialY ?? props.timelineHeight ? window.innerHeight - props.timelineHeight - 100 : window.innerHeight * 0.75); // 0.0 to 1.0 (percentage of parent height)

    const switchMode = (mode) => {
        if (props.setCurrentMode) {
            props.setCurrentMode(mode);
        }
    };

    const isCombinedMode = ![
        MODES.FLOW_EDITOR,
        MODES.CANVAS_EDITOR,
        MODES.EXPANDED_FLOW,
    ].includes(props.currentMode);

    const canOpenComponentFlow = Boolean(props.canOpenComponentFlow);
    const canOpenCanvas = Boolean(props.canOpenCanvas);

    const handleComponentFlowClick = (e) => {
        e.stopPropagation();
        if (isCombinedMode || !canOpenComponentFlow || !props.onOpenComponentFlow) {
            return;
        }
        props.onOpenComponentFlow();
    };

    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const toolbarRef = useRef(null);

    const onMouseDown = (e) => {
        isDragging.current = true;
        const rect = toolbarRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - (rect.left + rect.width / 2),
            y: e.clientY - (rect.top + rect.height / 2),
        };
        e.stopPropagation();
        e.preventDefault();

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        // set cursor to grabbing
        document.body.style.cursor = 'grabbing';
        toolbarRef.current.style.cursor = 'grabbing';

        // Prevent the flow area from capturing mouse events while dragging so it doesn't interfere
        const flowAreaElement = document.getElementById('flow-area-wrapper');
        if (flowAreaElement) {
            flowAreaElement.style.pointerEvents = 'none';
        }
    };

    const onMouseMove = (e) => {
        if (isDragging.current) {
            const parentRect = toolbarRef.current.parentElement.getBoundingClientRect();
            let newX = e.clientX - parentRect.left - dragOffset.current.x;
            let newY = e.clientY - parentRect.top - dragOffset.current.y;
            // Constrain within parent bounds
            newX = Math.max(0, Math.min(newX, parentRect.width - toolbarRef.current.offsetWidth));
            newY = Math.max(0, Math.min(newY, parentRect.height - toolbarRef.current.offsetHeight));
            setToolbarX(newX);
            setToolbarY(newY);
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const onMouseUp = (e) => {
        if (isDragging.current) {
            isDragging.current = false;
            e.stopPropagation();
            e.preventDefault();

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // reset cursor
            document.body.style.cursor = 'default';
            toolbarRef.current.style.cursor = 'grab';

            // Re-enable pointer events on the flow area
            const flowAreaElement = document.getElementById('flow-area-wrapper');
            if (flowAreaElement) {
                flowAreaElement.style.pointerEvents = 'auto';
            }
        }
    }

    // console.log('Rendering FloatingToolbar at ', toolbarX, toolbarY, props);

    return (
        <div 
            className="timeline-editor-floating-toolbar"
            style={{
                left: toolbarX,
                top: toolbarY,
            }}
            ref={toolbarRef}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
        >
            {/* <img src={HandStop} className="icon" alt="Hand Stop" /> */}
            {/* <div className="icon-divider" />
            <img src={Select} className="icon" alt="Select" /> */}
            {/* <div className="icon-divider" /> */}
            <img src={Undo} className={`icon ${props.undoStackLength === 0 ? 'disabled' : ''}`} alt="Undo" onClick={(e) => {e.stopPropagation(); props.undoLastAction && props.undoLastAction();}} />
            <img src={Redo} className={`icon ${props.redoStackLength === 0 ? 'disabled' : ''}`} alt="Redo" onClick={(e) => {e.stopPropagation(); props.redoLastAction && props.redoLastAction();}} />
            <div className="icon-divider" />

            <div className="mode-switcher-holder">
                <div className={`mode-switch-button ${props.currentMode === MODES.FLOW_EDITOR ? 'active' : ''}`} onClick={(e) => {e.stopPropagation(); switchMode(MODES.FLOW_EDITOR)}}>Flow</div>
                <div className={`mode-switch-button ${props.currentMode === MODES.CANVAS_EDITOR ? 'active' : ''} ${(isCombinedMode || !canOpenCanvas) ? 'disabled' : ''}`} onClick={(e) => {
                    e.stopPropagation();
                    if (isCombinedMode || !canOpenCanvas) {
                        return;
                    }
                    switchMode(MODES.CANVAS_EDITOR);
                }}>Canvas</div>
                <div className={`mode-switch-button ${props.currentMode === MODES.EXPANDED_FLOW ? 'active' : ''} ${(isCombinedMode || !canOpenComponentFlow) ? 'disabled' : ''}`} onClick={handleComponentFlowClick}>Component Flow</div>
            </div>
        </div>
    );
};

export default FloatingToolbar;